import { colors } from '@ctw/config/zui';
import {
  emptyTableMessage,
  filterChildNodesByNodeName,
  textContentFromNodeTree,
  uniqueIdentifierForNode,
} from '@ctw/shared/utils/cda/helpers';
import type { CdaParsedHtmlElementNode } from '@ctw/shared/utils/cda/types';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { max } from 'lodash-es';
import { memo } from 'react';
import { PdfNodeRenderer } from './pdf-node-renderer';

const MAX_HORIZONTAL_COLUMN_COUNT = 3;

// React PDF uses points (pt) for font sizing, but we think in terms of rems for consistency with the rest of the app.
const remToPts = (rem: number) => Math.floor(rem * 4 * 0.75);

export const tableStyles = StyleSheet.create({
  table: {
    width: '100%',
    marginTop: 0,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: colors.background.subtle,
    fontWeight: 400,
    fontSize: 12,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  rowFirstChild: {
    display: 'flex',
    flexDirection: 'row',
  },
  rowNotFirstChild: {
    display: 'flex',
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: colors.background.subtle,
  },
  header: {
    fontWeight: 500,
    fontSize: 12,
    paddingVertical: remToPts(1),
    letterSpacing: '0.05rem',
    color: colors.meta.text,
    alignItems: 'center',
  },
  cell: {
    padding: remToPts(2),
    fontWeight: 400,
  },
  cardContainer: {
    width: '100%',
    marginVertical: 2,
    borderWidth: 1,
    borderColor: colors.background.subtle,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  cardFirstChild: {
    padding: 8,
  },
  cardNotFirstChild: {
    borderTopWidth: 1,
    borderColor: colors.background.subtle,
    padding: 8,
  },
  cardField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingVertical: 3,
  },
  cardFieldLabel: {
    fontWeight: 500,
    fontSize: 12,
    letterSpacing: '0.05rem',
    color: colors.meta.text,
  },
  cardFieldValue: {
    fontSize: 11,
  },
});

interface PdfTableRendererProps {
  id: string;
  table: CdaParsedHtmlElementNode;
}

export const PdfTableRenderer = memo(({ id, table }: PdfTableRendererProps) => {
  const theadElement: CdaParsedHtmlElementNode | undefined = table.childNodes.find(
    (child) => child.nodeType === 'element' && child.nodeName === 'thead',
  ) as CdaParsedHtmlElementNode | undefined;
  const tbodyElement: CdaParsedHtmlElementNode | undefined = table.childNodes.find(
    (child) => child.nodeType === 'element' && child.nodeName === 'tbody',
  ) as CdaParsedHtmlElementNode | undefined;

  const candidateHeaderRows = theadElement ? theadElement.childNodes : table.childNodes;
  const headerRow = candidateHeaderRows
    // We only support one column header row right now, so pick the one with the most header cells
    // which is typically the _real_ headers, as opposed to rows with fewer headers which are typically
    // rows abused as table headings
    .sort(
      (aRow, bRow) =>
        filterChildNodesByNodeName(bRow, ['th'], false).length -
        filterChildNodesByNodeName(aRow, ['th'], false).length,
    )
    .find(
      (row) =>
        row.nodeType === 'element' &&
        row.nodeName === 'tr' &&
        !row.childNodes.some((child) => child.nodeType === 'element' && child.nodeName === 'td'),
    ) as CdaParsedHtmlElementNode | undefined;
  const columnThElements: Array<CdaParsedHtmlElementNode> = headerRow
    ? filterChildNodesByNodeName(headerRow, ['th'], false)
    : [];

  const candidateRecordRows = tbodyElement ? tbodyElement.childNodes : table.childNodes;
  const rows = candidateRecordRows.filter(
    (row) =>
      row.nodeType === 'element' &&
      row.nodeName === 'tr' &&
      row.childNodes.some((child) => child.nodeType === 'element' && child.nodeName === 'td'),
  ) as Array<CdaParsedHtmlElementNode>;

  const CONTENT_CHAR_WIDTH = 7.6; // Avg char width in pts for 12pt Inter, sentence case
  const HEADER_CHAR_WIDTH = CONTENT_CHAR_WIDTH * 1.5; // Boost for uppercase and letter spacing

  const headerMinWidth = (header: CdaParsedHtmlElementNode) => {
    const content = textContentFromNodeTree(header).trim();
    const maxCharsOnLine =
      max(
        content.split(' ').map((word) => {
          if (word.length > 15) {
            return 8;
          }
          return word.length;
        }),
      ) ?? 0;
    return maxCharsOnLine * HEADER_CHAR_WIDTH;
  };
  const contentMinWidth = (content: string) => {
    const maxCharsOnLine =
      max(
        content.split(' ').map((word) => {
          if (word.length > 15) {
            return 8;
          }
          return word.length;
        }),
      ) ?? 0;
    return maxCharsOnLine * CONTENT_CHAR_WIDTH;
  };

  // Calculate column widths based on header content
  const minColWidths = columnThElements.map((header) => headerMinWidth(header));

  let hasHorizontalHeaders = false;

  // Account for horizontal headers and cell content
  for (const row of rows) {
    for (const [colIndex, cell] of row.childNodes.entries()) {
      if (colIndex < minColWidths.length) {
        const cellContent = textContentFromNodeTree(cell).trim();
        const cellWidth =
          cell.nodeName === 'th' ? headerMinWidth(cell) : contentMinWidth(cellContent);
        minColWidths[colIndex] = Math.max(minColWidths[colIndex], cellWidth);

        if (cell.nodeName === 'th') {
          hasHorizontalHeaders = true;
        }
      }
    }
  }

  const totalMinWidth = minColWidths.reduce((sum, width) => sum + width, 0);
  const availableWidth = 595 - 60 - remToPts(4); // Standard A4 width (595pt) - margins (30pt each side) - padding
  const columnWidths = minColWidths.map(
    // Scale proportional to content
    (minWidth) => (minWidth * availableWidth) / (totalMinWidth || 1),
  );

  if (hasHorizontalHeaders || columnThElements.length <= MAX_HORIZONTAL_COLUMN_COUNT) {
    return (
      <View id={id} key={id} style={tableStyles.table} wrap={true} minPresenceAhead={150}>
        {/* Header row */}
        {columnThElements.some((column) => !column.isEffectivelyEmpty) && (
          <View
            style={[tableStyles.rowFirstChild, { display: 'flex', alignItems: 'center' }]}
            wrap={false}
            minPresenceAhead={40}
          >
            {columnThElements.map((header, index) => (
              <View
                key={uniqueIdentifierForNode({
                  node: header,
                  context: id,
                  purpose: 'table-header',
                  index,
                })}
                style={[tableStyles.cell, { width: columnWidths[index] }]}
                wrap={true}
                minPresenceAhead={40}
              >
                <Text style={tableStyles.header}>
                  {textContentFromNodeTree(header).trim().toUpperCase()}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Data rows */}
        {rows.every((row) => row.isEffectivelyEmpty) ? (
          <Text
            style={{
              marginTop: remToPts(4),
              marginBottom: remToPts(7),
              textAlign: 'center',
              width: '100%',

              color: colors.meta.text,
            }}
          >
            {emptyTableMessage}
          </Text>
        ) : (
          rows.map((row, rowIndex) => {
            const rowIdentifier = uniqueIdentifierForNode({
              node: row,
              context: id,
              purpose: 'table-row',
              index: rowIndex,
            });

            return (
              <View
                key={rowIdentifier}
                style={tableStyles.rowNotFirstChild}
                wrap={true}
                minPresenceAhead={120}
              >
                {row.childNodes.map((cell, cellIndex) => {
                  if (cell.nodeType !== 'element') {
                    return null;
                  }

                  const cellIdentifier = uniqueIdentifierForNode({
                    node: cell,
                    context: rowIdentifier,
                    purpose: 'table-cell',
                    index: cellIndex,
                  });

                  if (cell.childNodes.length > 1) {
                    cell.childNodes = [
                      {
                        nodeType: 'element',
                        nodeName: 'div',
                        childNodes: cell.childNodes,
                        isEffectivelyEmpty: cell.childNodes.every(
                          (child) => child.isEffectivelyEmpty,
                        ),
                        attributes: {},
                        styles: {
                          pdfStyles: {},
                          className: '',
                          extensions: [],
                        },
                      },
                    ];
                  }

                  if (cell.childNodes[0]) {
                    return (
                      <View
                        wrap={true}
                        key={cellIdentifier}
                        style={[tableStyles.cell, { width: columnWidths[cellIndex] }]}
                      >
                        {cell.nodeName === 'th' ? (
                          <Text style={[tableStyles.header]}>
                            {textContentFromNodeTree(cell).trim().toUpperCase()}
                          </Text>
                        ) : (
                          <PdfNodeRenderer
                            id={cellIdentifier}
                            key={cellIdentifier}
                            node={cell.childNodes[0]}
                          />
                        )}
                      </View>
                    );
                  }

                  return null;
                })}
              </View>
            );
          })
        )}
      </View>
    );
  }

  return (
    <View id={id} key={id} style={tableStyles.cardContainer} minPresenceAhead={150}>
      {/* Data rows */}
      {rows.every((row) => row.isEffectivelyEmpty) ? (
        <Text
          style={{
            marginTop: remToPts(4),
            marginBottom: remToPts(7),
            textAlign: 'center',
            width: '100%',

            color: colors.meta.text,
          }}
        >
          {emptyTableMessage}
        </Text>
      ) : (
        rows.map((row, rowIndex) => {
          const cardIdentifier = uniqueIdentifierForNode({
            node: row,
            context: id,
            purpose: 'table-card',
            index: rowIndex,
          });

          return (
            <View
              key={cardIdentifier}
              style={rowIndex === 0 ? tableStyles.cardFirstChild : tableStyles.cardNotFirstChild}
            >
              {row.childNodes.map((cell, cellIndex) => {
                if (cell.nodeType !== 'element') {
                  return null;
                }

                const fieldIdentifier = uniqueIdentifierForNode({
                  node: cell,
                  context: cardIdentifier,
                  purpose: 'table-card-field',
                  index: cellIndex,
                });

                if (cell.childNodes.length > 1) {
                  cell.childNodes = [
                    {
                      nodeType: 'element',
                      nodeName: 'div',
                      childNodes: cell.childNodes,
                      isEffectivelyEmpty: cell.childNodes.every(
                        (child) => child.isEffectivelyEmpty,
                      ),
                      attributes: {},
                      styles: {
                        pdfStyles: {},
                        className: '',
                        extensions: [],
                      },
                    },
                  ];
                }

                if (cell.childNodes[0] && cell.nodeName !== 'th') {
                  return (
                    <View
                      key={fieldIdentifier}
                      minPresenceAhead={120}
                      style={tableStyles.cardField}
                      wrap={true}
                    >
                      <Text style={tableStyles.cardFieldLabel} wrap={false}>
                        {columnThElements.at(cellIndex)
                          ? textContentFromNodeTree(
                              columnThElements.at(cellIndex) as CdaParsedHtmlElementNode,
                            )
                              .trim()
                              .toUpperCase()
                          : ''}
                      </Text>
                      <View style={tableStyles.cardFieldValue} wrap={true}>
                        <PdfNodeRenderer
                          id={fieldIdentifier}
                          key={fieldIdentifier}
                          node={cell.childNodes[0]}
                        />
                      </View>
                    </View>
                  );
                }

                return null;
              })}
            </View>
          );
        })
      )}
    </View>
  );
});
